import { useCallback, useState } from 'react'
import {
  defaultPaymentApproved,
  deletePaymentLog,
  saveClaimReviewLog,
  saveDefaultPayment,
} from '../services/residentApi'
import type { ReviewApprovalField, ReviewApprovalValue } from '../types'

/**
 * Mirrors Angular's `savePayment`/`saveClaim` request shape.
 */
function buildApprovalRequest(
  field: ReviewApprovalField,
  value: ReviewApprovalValue,
  applicationId?: string | number,
  rejectReason?: string,
): Record<string, unknown> {
  const requestData: Record<string, unknown> = { page: 'default' }
  if (applicationId) requestData.application_id = applicationId

  let reviewDecision: string | undefined
  if (value === '0') {
    reviewDecision = field === 'insurance_review_approval' ? 'Returned to L2' : 'Returned to L1'
  } else if (value === '1' && field === 'l1_approved') {
    reviewDecision = 'Recalculated'
  } else if (value === '1' && (field === 'liberty_approved' || field === 'insurance_review_approval')) {
    reviewDecision = 'Approved'
  } else if (value === '2') {
    reviewDecision =
      field === 'liberty_approved' || field === 'liberty_authorization_approval'
        ? 'Escalated to L3 Review'
        : 'Resubmitted to L2'
  } else if (value === '3') {
    reviewDecision = 'Recalculated'
  } else if (value === '4') {
    reviewDecision = 'Resubmitted to L3'
  }
  if (reviewDecision) requestData.review_decision = reviewDecision

  if (rejectReason) {
    requestData.is_rejected = '1'
    requestData.review_remark = rejectReason
  }
  return requestData
}

export function useResidentPayments(
  defaultId?: string | number,
  onRefresh?: () => Promise<void> | void,
  applicationId?: string | number,
) {
  const [processing, setProcessing] = useState(false)

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setProcessing(true)
      try {
        const response = await action()
        await onRefresh?.()
        return response
      } finally {
        setProcessing(false)
      }
    },
    [onRefresh],
  )

  const savePayment = useCallback(
    (payload: Record<string, unknown>) => {
      if (!defaultId) return Promise.resolve()
      return run(() => saveDefaultPayment(defaultId, payload))
    },
    [defaultId, run],
  )

  const approvePayment = useCallback(
    (id: string | number, payload: Record<string, unknown>) => run(() => defaultPaymentApproved(id, payload)),
    [run],
  )

  /**
   * Approve / Reject / Escalate a payment review log row using Angular's
   * `savePayment` semantics: posts to `/api/default_payment_approved/:id`
   * with `{ approval, approval_flag, review_decision, is_rejected?, review_remark? }`.
   */
  const submitPaymentApproval = useCallback(
    async (
      paymentLogId: string | number,
      field: ReviewApprovalField,
      value: ReviewApprovalValue,
      rejectReason?: string,
    ) => {
      const request = buildApprovalRequest(field, value, applicationId, rejectReason)
      request.approval = value
      request.approval_flag =
        field === 'liberty_approved'
          ? 'liberty'
          : field === 'insurance_review_approval'
            ? 'insurance'
            : 'l1_approval'
      return run(() => defaultPaymentApproved(paymentLogId, request))
    },
    [applicationId, run],
  )

  /**
   * Approve / Reject a claim review log row using Angular's `saveClaim`
   * semantics: posts to `/api/claim_review_log/:id` with
   * `{ [field]: value, review_decision, is_rejected?, review_remark? }`.
   */
  const submitClaimReviewApproval = useCallback(
    async (
      reviewLogId: string | number,
      field: ReviewApprovalField,
      value: ReviewApprovalValue,
      rejectReason?: string,
    ) => {
      const request = buildApprovalRequest(field, value, applicationId, rejectReason)
      request[field] = value
      return run(() => saveClaimReviewLog(reviewLogId, request))
    },
    [applicationId, run],
  )

  const saveReviewLog = useCallback(
    (id: string | number, payload: Record<string, unknown>) => run(() => saveClaimReviewLog(id, payload)),
    [run],
  )

  const removePayment = useCallback((id: string | number) => run(() => deletePaymentLog(id)), [run])

  return {
    processing,
    savePayment,
    approvePayment,
    saveReviewLog,
    removePayment,
    submitPaymentApproval,
    submitClaimReviewApproval,
  }
}
